import React, { useEffect, useState } from 'react';
import { 
    Grid, Card, CardContent, Typography, LinearProgress, Box, Button, 
    Chip, List, ListItem, ListItemText, ListItemIcon, Divider, CircularProgress, Dialog, DialogContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { getWorksListAWS } from '../offchain-logic/services';
import moment from 'moment';
import '../App.css';

interface DashboardProps {
    handleOpenToast: (message: string, severity: 'success' | 'error') => void;
    documentList: any[];
}

interface WorkItem {
    id_work: string;
    work_name: string;
    status: string;
    completion_percentage: number;
    completion_count: number;
    total_required: number;
    created_at: string;
    work_date?: string;
    id_activity: string;
}

export const Dashboard: React.FC<DashboardProps> = ({ handleOpenToast, documentList }) => {
    const navigate = useNavigate();
    const [allWorks, setAllWorks] = useState<WorkItem[]>([]);
    const [selectedActivityId, setSelectedActivityId] = useState<string>('');
    const [activitiesLoading, setActivitiesLoading] = useState(false);

    // Statistiche calcolate
    const totalWorks = allWorks.length;
    const completedWorks = allWorks.filter(w => w.status === 'completed').length;
    const inProgressWorks = allWorks.filter(w => w.status === 'in_progress').length;
    const pendingWorks = allWorks.filter(w => w.status === 'pending').length;
    
    // Totali documenti obbligatori
    const totalDocsUploaded = allWorks.reduce((sum, w) => sum + (w.completion_count || 0), 0);
    const totalDocsRequired = allWorks.reduce((sum, w) => sum + (w.total_required || 0), 0);
    
    // Media pesata basata sui documenti totali
    const averageCompletion = totalDocsRequired > 0 
        ? Math.round((totalDocsUploaded / totalDocsRequired) * 100)
        : 0;

    const totalDocuments = documentList.length;

    useEffect(() => {
        const loadDashboardData = async (activityId: string) => {
            console.log('📊 [DASHBOARD] Loading data for activity:', activityId);
            
            try {
                // Usa getWorksListAWS (stesso di Gestione pratiche che funziona)
                getWorksListAWS(activityId, (err: any, data: any) => {
                    if (err) {
                        console.error('❌ [DASHBOARD] Errore nel caricamento delle pratiche:', err);
                        handleOpenToast('Errore nel caricamento delle pratiche', 'error');
                        return;
                    }
                    
                    console.log('📊 [DASHBOARD] Response from getWorksListAWS:', data);
                    const works = data.works || [];
                    console.log('📊 [DASHBOARD] Pratiche loaded:', works);
                    console.log('📊 [DASHBOARD] Number of pratiche:', works.length);
                    setAllWorks(works);
                });
                
            } catch (error) {
                console.error('❌ [DASHBOARD] Errore nel caricamento delle pratiche:', error);
                handleOpenToast('Errore nel caricamento delle pratiche', 'error');
            }
        };
        
        // Ascolta l'evento activityChanged dall'Header
        const handleActivityChanged = (event: any) => {
            const activityId = event.detail;
            if (activityId) {
                setSelectedActivityId(activityId);
                loadDashboardData(activityId);
            }
        };

        window.addEventListener('activityChanged', handleActivityChanged);
        const handleActivitiesLoading = (event: Event) => {
            setActivitiesLoading((event as CustomEvent<boolean>).detail);
        };
        window.addEventListener('activitiesLoading', handleActivitiesLoading);

        // Prova a caricare subito se l'activity è già in sessionStorage (es: refresh pagina)
        const activityId = sessionStorage.getItem('selectedActivityId');
        const storedActivities = JSON.parse(sessionStorage.getItem('activities') || '[]');
        const hasCurrentActivity = Array.isArray(storedActivities) &&
            storedActivities.some((activity: any) => activity.activityId === activityId);
        const isAdminContext = Boolean(sessionStorage.getItem('selectedUserId'));
        if (activityId && (hasCurrentActivity || isAdminContext)) {
            setSelectedActivityId(activityId);
            loadDashboardData(activityId);
        }

        return () => {
            window.removeEventListener('activityChanged', handleActivityChanged);
            window.removeEventListener('activitiesLoading', handleActivitiesLoading);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // handleOpenToast viene omesso dalle dipendenze per evitare loop infiniti

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'success';
            case 'in_progress': return 'primary';
            case 'pending': return 'warning';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed': return 'Completato';
            case 'in_progress': return 'In Corso';
            case 'pending': return 'In Attesa';
            case 'cancelled': return 'Annullato';
            default: return status;
        }
    };

    // Lavori recenti (ultimi 5)
    const recentWorks = [...allWorks]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5);

    return (
        <>
            <Dialog open={activitiesLoading} maxWidth="xs" fullWidth>
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="h6">Caricamento attività</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Stiamo sincronizzando i dati del pagamento.
                    </Typography>
                </DialogContent>
            </Dialog>
            <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#8B6F47' }}>
                    Dashboard
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Panoramica generale delle tue pratiche e documenti
                </Typography>
            </Box>

            {!selectedActivityId ? (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <WorkOutlineIcon sx={{ fontSize: 80, color: '#ccc', mb: 3 }} />
                    <Typography variant="h5" gutterBottom color="textSecondary">
                        Nessuna attività selezionata
                    </Typography>
                    <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                        Seleziona un'attività dall'header per visualizzare la dashboard
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* Statistiche principali */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {/* Totale Pratiche */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <WorkOutlineIcon sx={{ fontSize: 40, color: '#20679F', mr: 2 }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                                {totalWorks}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Pratiche Totali
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Pratiche in Attesa */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <HourglassEmptyIcon sx={{ fontSize: 40, color: '#ff9800', mr: 2 }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                                {pendingWorks}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                In Attesa
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Pratiche in Corso */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <PlayCircleOutlineIcon sx={{ fontSize: 40, color: '#2196f3', mr: 2 }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                                {inProgressWorks}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                In Corso
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Pratiche Completate */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <CheckCircleOutlineIcon sx={{ fontSize: 40, color: '#4caf50', mr: 2 }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                                {completedWorks}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Completate
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Completamento Medio */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                        <TrendingUpIcon sx={{ mr: 1, color: '#20679F' }} />
                                        Completamento Medio Pratiche
                                    </Typography>
                                    <Box sx={{ mt: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" color="textSecondary">
                                                Progresso complessivo
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {averageCompletion}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={averageCompletion} 
                                            sx={{ 
                                                height: 10, 
                                                borderRadius: 5,
                                                backgroundColor: '#e0e0e0',
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: averageCompletion === 100 ? '#4caf50' : '#20679F'
                                                }
                                            }} 
                                        />
                                        <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                                            <Chip 
                                                icon={<CheckCircleOutlineIcon />}
                                                label={`${totalDocsUploaded}/${totalDocsRequired} Documenti caricati`}
                                                size="small"
                                                sx={{ 
                                                    bgcolor: totalDocsUploaded === totalDocsRequired ? '#e8f5e9' : '#e3f2fd',
                                                    color: totalDocsUploaded === totalDocsRequired ? '#2e7d32' : '#1565c0',
                                                    fontWeight: 500,
                                                    '& .MuiChip-icon': {
                                                        color: totalDocsUploaded === totalDocsRequired ? '#2e7d32' : '#1565c0'
                                                    }
                                                }}
                                            />
                                            <Chip 
                                                icon={<InsertDriveFileOutlinedIcon />}
                                                label={`${totalDocsRequired} Obbligatori`}
                                                size="small"
                                                sx={{ 
                                                    bgcolor: '#f5f5f5',
                                                    color: '#616161',
                                                    fontWeight: 500,
                                                    '& .MuiChip-icon': {
                                                        color: '#616161'
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Accessi Rapidi */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Accessi Rapidi
                                    </Typography>
                                    <Box sx={{ mt: 3 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => navigate('/create-work')}
                                            sx={{ mb: 2, py: 1.5 }}
                                        >
                                            Nuova Pratica
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={<WorkOutlineIcon />}
                                            onClick={() => navigate('/works')}
                                            sx={{ py: 1.5 }}
                                        >
                                            Tutte le Pratiche
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Pratiche Recenti */}
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Pratiche Recenti
                                    </Typography>
                                    {recentWorks.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <WorkOutlineIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                                            <Typography variant="body1" color="textSecondary">
                                                Nessuna pratica presente
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <List>
                                            {recentWorks.map((work, index) => (
                                                <React.Fragment key={work.id_work}>
                                                    <ListItem
                                                        button
                                                        onClick={() => navigate(`/work-detail/${work.id_work}`)}
                                                        sx={{ 
                                                            '&:hover': { backgroundColor: '#f5f5f5' },
                                                            borderRadius: 1,
                                                            mb: 1
                                                        }}
                                                    >
                                                        <ListItemIcon>
                                                            <WorkOutlineIcon color="primary" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                                        {work.work_name}
                                                                    </Typography>
                                                                    <Chip 
                                                                        label={getStatusLabel(work.status)} 
                                                                        color={getStatusColor(work.status) as any}
                                                                        size="small"
                                                                    />
                                                                </Box>
                                                            }
                                                            secondary={
                                                                <React.Fragment>
                                                                    <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 1 }}>
                                                                        Creato il {moment(work.created_at, 'DD/MM/YYYY').format('DD/MM/YYYY')}
                                                                        {work.work_date && ` • Data pratica: ${moment(work.work_date, 'DD/MM/YYYY').format('DD/MM/YYYY')}`}
                                                                    </Typography>
                                                                    <Box sx={{ mt: 1 }}>
                                                                        <LinearProgress 
                                                                            variant="determinate" 
                                                                            value={work.completion_percentage} 
                                                                            sx={{ 
                                                                                height: 6, 
                                                                                borderRadius: 3,
                                                                                backgroundColor: '#e0e0e0',
                                                                                '& .MuiLinearProgress-bar': {
                                                                                    backgroundColor: work.completion_percentage === 100 ? '#4caf50' : '#20679F'
                                                                                }
                                                                            }} 
                                                                        />
                                                                        <Typography variant="caption" component="div" sx={{ mt: 0.5, display: 'block' }}>
                                                                            Completamento: {work.completion_percentage}%
                                                                        </Typography>
                                                                    </Box>
                                                                </React.Fragment>
                                                            }
                                                            secondaryTypographyProps={{ component: 'div' }}
                                                        />
                                                    </ListItem>
                                                    {index < recentWorks.length - 1 && <Divider />}
                                                </React.Fragment>
                                            ))}
                                        </List>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}
            </Box>
        </>
    );
};
